/**
 * /erp/dispatches — list of dispatches (Manager+ for PR-F).
 *
 * Two simple filters: status (draft/confirmed/in_transit/delivered/
 * cancelled) and a free-text search across code, driver, transport,
 * recipients, and CRM-link names. Status defaults to "all" because
 * the day-to-day workflow alternates between "what's outstanding"
 * (draft + confirmed + in_transit) and "what's done" (delivered),
 * and a quick toggle is more useful than a default narrowing.
 *
 * Each row links to the detail page where the lifecycle buttons live.
 * A "New dispatch" CTA opens the create form. Empty state distinguishes
 * "no dispatches yet" from "no matches for this filter".
 */

import { Link, createFileRoute } from "@tanstack/react-router";
import { Filter, Loader2, Plus, Search, Truck, Warehouse } from "lucide-react";
import { useMemo, useState } from "react";

import type { ErpDispatchRead, ErpDispatchStatus } from "../lib/api";
import { useDispatches } from "../lib/erp";

export const Route = createFileRoute("/_app/erp/dispatches/")({
  component: DispatchListPage,
});

const STATUSES: { id: ErpDispatchStatus | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
  { id: "confirmed", label: "Confirmed" },
  { id: "in_transit", label: "In transit" },
  { id: "delivered", label: "Delivered" },
  { id: "cancelled", label: "Cancelled" },
];

function DispatchListPage() {
  const [statusFilter, setStatusFilter] = useState<ErpDispatchStatus | "all">("all");
  const [search, setSearch] = useState("");

  const dispatchesQ = useDispatches(statusFilter === "all" ? {} : { status: statusFilter });

  const filtered = useMemo(() => {
    const items = dispatchesQ.data?.items ?? [];
    if (!search.trim()) return items;
    const needle = search.trim().toLowerCase();
    return items.filter((d) => {
      const haystack = [
        d.code,
        d.driver_code,
        d.driver_name,
        d.temp_driver_name,
        d.transport_code,
        d.transport_vehicle_number,
        d.temp_transport_name,
        d.deal_title,
        d.account_name,
        d.contact_name,
        d.notes,
        ...d.deliveries.flatMap((dlv) => [
          dlv.account_name,
          dlv.contact_name,
          dlv.source_warehouse_code,
          dlv.dest_warehouse_code,
        ]),
      ];
      return haystack.some((s) => s?.toLowerCase().includes(needle));
    });
  }, [dispatchesQ.data?.items, search]);

  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Truck className="size-5 opacity-70" aria-hidden />
            Dispatches
          </h1>
          <p className="text-sm opacity-60">
            Outbound runs to customers and inter-warehouse moves. Each dispatch can carry many
            deliveries — internal, external, or both.
          </p>
        </div>
        <Link
          to="/erp/dispatches/new"
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          <Plus className="size-4" aria-hidden />
          New dispatch
        </Link>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] p-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium opacity-80">
            <Filter className="mr-1 inline size-3" aria-hidden />
            Status
          </span>
          <div className="flex flex-wrap gap-1">
            {STATUSES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStatusFilter(s.id)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  statusFilter === s.id
                    ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                    : "border-[var(--border)] hover:bg-black/5 dark:hover:bg-white/5"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <label className="flex flex-1 flex-col gap-1">
          <span className="text-xs font-medium opacity-80">Search</span>
          <div className="flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2 py-1.5 focus-within:border-[var(--accent)]">
            <Search className="size-4 opacity-50" aria-hidden />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Code, driver, transport, recipient, warehouse, or CRM link"
              className="w-full bg-transparent text-sm outline-none"
            />
          </div>
        </label>
      </div>

      {dispatchesQ.isLoading ? (
        <p className="flex items-center gap-2 text-sm opacity-60">
          <Loader2 className="size-4 animate-spin" aria-hidden /> Loading dispatches…
        </p>
      ) : dispatchesQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load dispatches: {(dispatchesQ.error as Error).message}
        </p>
      ) : filtered.length === 0 ? (
        <EmptyState
          hasFilter={statusFilter !== "all" || !!search.trim()}
          totalDispatches={dispatchesQ.data?.total ?? 0}
        />
      ) : (
        <DispatchTable dispatches={filtered} />
      )}
    </div>
  );
}

function EmptyState({
  hasFilter,
  totalDispatches,
}: {
  hasFilter: boolean;
  totalDispatches: number;
}) {
  return (
    <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
      <Truck className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
      {hasFilter && totalDispatches > 0 ? (
        <>
          <p className="text-sm font-medium">No matching dispatches.</p>
          <p className="mt-1 text-sm opacity-60">Clear the filter or try a different search.</p>
        </>
      ) : (
        <>
          <p className="text-sm font-medium">No dispatches yet.</p>
          <p className="mt-1 text-sm opacity-60">
            Create one to plan an outbound run. Drafts can be edited freely; once confirmed, the
            line composition freezes for audit-trail integrity.
          </p>
          <Link
            to="/erp/dispatches/new"
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="size-4" aria-hidden />
            Create your first dispatch
          </Link>
        </>
      )}
    </div>
  );
}

function DispatchTable({ dispatches }: { dispatches: ErpDispatchRead[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
      <table className="w-full text-sm">
        <thead className="border-b border-[var(--border)] bg-black/[0.02] text-left text-xs uppercase tracking-wider opacity-70 dark:bg-white/[0.02]">
          <tr>
            <th className="px-3 py-2">Code</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Deliveries</th>
            <th className="px-3 py-2">Driver / Transport</th>
            <th className="px-3 py-2">Linked to</th>
            <th className="px-3 py-2 text-right">Total</th>
            <th className="px-3 py-2">Created</th>
          </tr>
        </thead>
        <tbody>
          {dispatches.map((d) => (
            <tr
              key={d.id}
              className="border-b border-[var(--border)] last:border-0 hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
            >
              <td className="px-3 py-2">
                <Link
                  to="/erp/dispatches/$dispatchId"
                  params={{ dispatchId: d.id }}
                  className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] text-[var(--accent)] hover:underline dark:bg-white/10"
                >
                  {d.code}
                </Link>
              </td>
              <td className="px-3 py-2">
                <StatusBadge status={d.status} />
              </td>
              <td className="px-3 py-2">
                <DeliveryCounts dispatch={d} />
              </td>
              <td className="px-3 py-2 text-xs">
                <DriverTransport dispatch={d} />
              </td>
              <td className="px-3 py-2 text-xs">
                <CrmLinks dispatch={d} />
              </td>
              <td className="px-3 py-2 text-right font-mono text-xs">
                {d.dispatch_total ? formatDispatchTotal(d.dispatch_total) : <span className="opacity-40">—</span>}
              </td>
              <td className="px-3 py-2 text-xs opacity-60">
                {new Date(d.created_at).toLocaleDateString()}
                {d.created_by_name && <span className="ml-1">by {d.created_by_name}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatDispatchTotal(value: string): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value;
  return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function DeliveryCounts({ dispatch }: { dispatch: ErpDispatchRead }) {
  const ext = dispatch.deliveries.filter((d) => d.kind === "external").length;
  const int = dispatch.deliveries.filter((d) => d.kind === "internal").length;
  if (ext === 0 && int === 0) {
    return <span className="text-xs opacity-50">No deliveries yet</span>;
  }
  return (
    <div className="flex flex-wrap gap-1 text-[11px]">
      {ext > 0 && (
        <span className="rounded-md bg-blue-100 px-1.5 py-0.5 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
          {ext} external
        </span>
      )}
      {int > 0 && (
        <span className="rounded-md bg-purple-100 px-1.5 py-0.5 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200">
          {int} internal
        </span>
      )}
    </div>
  );
}

function DriverTransport({ dispatch }: { dispatch: ErpDispatchRead }) {
  const driver = dispatch.driver_name || dispatch.temp_driver_name;
  const transport = dispatch.transport_vehicle_number || dispatch.temp_transport_name;
  if (!driver && !transport) {
    return <span className="opacity-50">—</span>;
  }
  return (
    <div className="space-y-0.5">
      {driver && <div className="opacity-90">{driver}</div>}
      {transport && (
        <div className="flex items-center gap-1 opacity-70">
          <Warehouse className="size-3" aria-hidden /> {transport}
        </div>
      )}
    </div>
  );
}

function CrmLinks({ dispatch }: { dispatch: ErpDispatchRead }) {
  const labels: string[] = [];
  if (dispatch.deal_title) labels.push(`Deal: ${dispatch.deal_title}`);
  if (dispatch.account_name) labels.push(`Acct: ${dispatch.account_name}`);
  if (dispatch.contact_name) labels.push(`Contact: ${dispatch.contact_name}`);
  if (labels.length === 0) {
    return <span className="opacity-50">—</span>;
  }
  return (
    <div className="space-y-0.5">
      {labels.map((l) => (
        <div key={l} className="opacity-80">
          {l}
        </div>
      ))}
    </div>
  );
}

export function StatusBadge({ status }: { status: ErpDispatchStatus }) {
  const styles: Record<ErpDispatchStatus, string> = {
    draft: "bg-black/10 text-black/70 dark:bg-white/10 dark:text-white/80",
    confirmed: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
    in_transit: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    delivered: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
    cancelled: "bg-red-100 text-red-800 opacity-80 dark:bg-red-900/40 dark:text-red-200",
  };
  const labels: Record<ErpDispatchStatus, string> = {
    draft: "Draft",
    confirmed: "Confirmed",
    in_transit: "In transit",
    delivered: "Delivered",
    cancelled: "Cancelled",
  };
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}
